import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const TOKEN_KEY = "cargo_admin_token";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const adminApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-logout on 401 (session expired)
adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      try { localStorage.removeItem(TOKEN_KEY); } catch (e) { console.warn("AuthContext: localStorage removeItem failed", e); }
    }
    return Promise.reject(err);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const r = await adminApi.get("/auth/me");
      setUser(r.data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const r = await adminApi.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.role === "owner") return true;
    return Array.isArray(user.permissions) && user.permissions.includes(perm);
  };

  const hasAnyPermission = (...perms) => perms.some((p) => hasPermission(p));

  const isOwner = () => user?.role === "owner";
  const isAdminOrOwner = () => user?.role === "owner" || user?.role === "admin";

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        hasPermission,
        hasAnyPermission,
        isOwner,
        isAdminOrOwner,
        refresh: fetchMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Er is iets misgegaan.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export const ROLE_LABELS = {
  owner: "Owner / Hoofdaccount",
  admin: "Admin",
  manager: "Manager",
  medewerker: "Medewerker",
  content_manager: "Content Manager",
  webshop_manager: "Webshop Manager",
};
